import { format } from 'date-fns'
import * as React from 'react'

import { usePagination } from 'hooks/usePagination'
import {
  useGetBranchesQuery,
  useGetRegionsQuery,
  useGetStatusesQuery,
} from 'redux/services/common'
import { NonResidentApi, NonResidentParams } from 'services/api/NonResidentApi'

import { AppLayoutTable } from 'components/AppLayoutTable'
import { AppLoader } from 'components/AppLoader'
import { AppPagination } from 'components/AppPagination'
import { AppStatistics } from 'components/AppStatistics/AppStatistics'
import { AppTable } from 'components/AppTable'

import { NonResidentObject, NonResidentShortObject } from 'models/nonresident'
import { IStatistic } from 'models/statistic'

import { formatDate } from 'shared/libs'
import { EmptyDetails } from 'shared/ui'

import { NonResidentDetails } from './components/NonResidentDetails'
import { NonResidentDetailsHeader } from './components/NonResidentDetailsHeader'
import { NonResidentImageModal } from './components/NonResidentImageModal'

export const NonResidentPage = () => {
  const { data: regions = [] } = useGetRegionsQuery()
  const { data: statuses = [] } = useGetStatusesQuery()
  const { data: branches = [] } = useGetBranchesQuery()

  const [disabled, setDisabled] = React.useState(false)
  const [details, setDetails] = React.useState<NonResidentObject>()

  const [lightbox, setLightbox] = React.useState<string[]>([])
  const [attachments, setAttachments] = React.useState<string[]>([])
  const [downloadingExel, setDownloadingExel] = React.useState(false)
  const [downloadingExelBonus, setDownloadingExelBonus] = React.useState(false)
  const [downloadingExelForMonth, setDownloadingExelForMonth] =
    React.useState(false)
  const { page, list, count, fetchingList, getList, setParams } = usePagination(
    NonResidentApi.getNonResidents,
  )
  const [statistics, setStatistics] = React.useState<IStatistic[]>([])

  const applyFilters = (filters: NonResidentParams) => {
    setParams(filters)
    getList(1, filters)
    fetchStatistics(filters)
  }

  async function fetchStatistics(params: NonResidentParams) {
    const response = await NonResidentApi.getNonResidentsStatistics(params)
    if (response.status === 200 && response.data) {
      setStatistics(response.data)
    }
  }

  const downloadNonResidents = async (filters: NonResidentParams) => {
    setDownloadingExel(true)
    try {
      const { data } = await NonResidentApi.downloadNonResidents(filters)
      // The actual download
      const filename = `reports_for_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (error) {}
    setDownloadingExel(false)
  }

  const downloadNonResidentsBonus = async (filters: NonResidentParams) => {
    setDownloadingExelBonus(true)
    try {
      const { data } = await NonResidentApi.downloadNonResidentsBonus(filters)
      // The actual download
      const filename = `reports_for_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (error) {}
    setDownloadingExelBonus(false)
  }

  const downloadNonResidentsForMonth = async (filters: NonResidentParams) => {
    setDownloadingExelForMonth(true)
    try {
      const { data } =
        await NonResidentApi.downloadNonResidentsForMonth(filters)
      // The actual download
      const filename = `reports_for_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (error) {}
    setDownloadingExelForMonth(false)
  }

  const openDetails = async (application: NonResidentShortObject) => {
    try {
      if (application.id === details?.id) {
        return
      }
      closeDetails()

      const { data } = await NonResidentApi.getNonResidentById(application.id)

      setDetails(data)
      const images = []

      if (data?.client_photo) {
        images.push(data.client_photo)
      }

      if (data?.passport) {
        images.push(data.passport)
      }

      if (data?.stay_document) {
        images.push(data.stay_document)
      }

      if (data?.notarized_passport) {
        images.push(data.notarized_passport)
      }
      if (data?.notarized_passport_back) {
        images.push(data.notarized_passport_back)
      }
      setAttachments(images)
    } catch (error) {}
  }
  const onAccept = async () => {
    try {
      setDisabled(true)
      await NonResidentApi.approveNonResident(details?.id as number)

      getList()
      closeDetails()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const onRefuse = async () => {
    try {
      setDisabled(true)
      await NonResidentApi.refuseNonResident(details?.id as number)

      getList()
      closeDetails()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const onRework = async (controller_comment: string) => {
    try {
      setDisabled(true)
      await NonResidentApi.reworkNonResident(details?.id as number, {
        controller_comment,
      })

      getList()
      closeDetails()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const openLightboxAttachments = (index: number) => {
    openLightbox(attachments)
  }

  const openLightbox = (image: string[]) => {
    document.body.style.overflow = 'hidden'

    setLightbox(image)
  }

  const closeLightbox = () => {
    document.body.style.overflow = 'auto'
    setLightbox([])
  }

  const closeDetails = () => {
    setDetails(undefined)
    setAttachments([])
  }

  React.useEffect(() => {
    fetchStatistics({} as NonResidentParams)
  }, [])

  return (
    <>
      <AppLayoutTable
        header={
          <NonResidentDetailsHeader
            onDownloadExcel={downloadNonResidents}
            onDownloadExcelBonus={downloadNonResidentsBonus}
            onDownloadExcelForMonth={downloadNonResidentsForMonth}
            downloadingExel={downloadingExel}
            downloadingExelBonus={downloadingExelBonus}
            downloadingExelForMonth={downloadingExelForMonth}
            onSubmit={applyFilters}
            branches={branches}
            regions={regions}
            statuses={statuses}
          />
        }
        sidebarOpened={Boolean(details)}
        sidebar={
          details ? (
            <NonResidentDetails
              disabled={disabled}
              attachments={attachments}
              onAccept={onAccept}
              openLivePhoto={() => openLightbox([details.client_photo])}
              openAttachments={openLightboxAttachments}
              setLightbox={setLightbox}
              onRework={onRework}
              onRefuse={onRefuse}
              onClose={closeDetails}
              application={details}
            />
          ) : (
            <EmptyDetails />
          )
        }
        statistics={
          statistics?.length > 0 ? <AppStatistics list={statistics} /> : null
        }
        title="Заявки"
      >
        {fetchingList ? (
          <AppLoader />
        ) : (
          <AppTable
            selected={{
              value: details?.id,
              key: 'id',
            }}
            onClickRow={openDetails}
            headers={[
              {
                data: 'ID',
                key: 'id',
                width: 50,
              },
              {
                data: 'Консультант',
                key: 'consultant',
                render: (_, data) => {
                  return `${data.consultant?.first_name || ''} ${
                    data.consultant?.last_name || ''
                  } ${data.consultant?.middle_name || ''}`
                },
              },
              {
                data: 'Ф.И.О',
                key: 'client_full_name',
              },
              {
                data: 'Дата подачи',
                key: 'created_at',
                render: (_, item) => formatDate(item.created_at),
              },
              {
                data: 'Статус',
                key: 'status',
                render: status => {
                  return statuses.find(item => item.code === status)?.name
                },
                width: 100,
              },
              {
                data: 'Регион',
                key: 'region',
                render: (_, data) => {
                  return data.region?.name
                },
                width: 80,
              },
              {
                data: 'ИНН',
                key: 'inn',
                render: (_, data) => {
                  return data.inn || ''
                },
                width: 70,
              },
              {
                data: 'ПАН',
                key: 'card',
                render: (_, data) => {
                  return data.card?.number || ''
                },
                width: 70,
              },
              {
                data: 'Комментарий',
                key: 'controller_comment',
                render: (_, data) => {
                  let comment = data?.controller_comment || ''
                  return comment
                },
                width: 70,
              },
            ]}
            dataSource={list}
          />
        )}
      </AppLayoutTable>
      {count > 0 ? (
        <AppPagination
          forcePage={page - 1}
          onPageChange={getList}
          count={count}
        />
      ) : null}
      {lightbox.length ? (
        <NonResidentImageModal onClose={closeLightbox} images={lightbox} />
      ) : null}
    </>
  )
}
